import React from "react";
import { Link } from "react-router-dom";
import { PageSection } from "@/components/AppLayout.jsx";
import { ModuleCard, GsdReadableBlock, DojoOsModulePlaceholder, useI18n } from "@/coquille-bridge";
import { useHybridDataStore } from "@/hooks/useHybridDataStore.js";
import { useRoleHybridContext } from "@/hooks/useRoleHybridContext.js";
import { BackendZoneBadge } from "@/components/BackendZoneBadge.jsx";
import { DemoBadge } from "@/components/DemoBadge.jsx";
import { useKarateUi } from "@/hooks/useKarateUi.js";

function useRoleContext() {
  const hybrid = useRoleHybridContext();
  const { store } = useHybridDataStore();
  const sensei = hybrid.dojo?.sensei_id ? store.instructeurs.find((i) => i.id === hybrid.dojo.sensei_id) : null;
  const sempai = hybrid.dojo?.sempai_id ? store.instructeurs.find((i) => i.id === hybrid.dojo.sempai_id) : null;
  return {
    ctx: hybrid.demoCtx,
    dojo: hybrid.dojo,
    sensei,
    sempai,
    students: hybrid.students,
    children: hybrid.children,
    fromBackend: hybrid.fromBackend,
    hasDojo: hybrid.hasDojo,
    hasChildren: hybrid.hasChildren,
  };
}

export function SempaiDashboardPage() {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const { localizeDemo, demoBadge, ui } = useKarateUi();
  const { dojo, students, fromBackend, hasDojo } = useRoleContext();
  return (
    <PageSection
      title={locale === "en" ? "Sempai space" : "Espace Sempai"}
      subtitle={locale === "en" ? "Assistant role — Phase 1 demo" : "Rôle assistant — démo Phase 1"}
      testId="page-sempai-dashboard"
    >
      <BackendZoneBadge variant={fromBackend ? "backendConnected" : "managerPrepared"} testId="sempai-dashboard-backend-badge" />
      <p className="warning-box" data-testid="sempai-limit-notice">{ui.roleLimits.sempaiLimited}</p>
      {!hasDojo ? (
        <p className="muted" data-testid="sempai-no-dojo">{ui.roleLimits.senseiNoDojo}</p>
      ) : (
        <p className="muted" data-testid="sempai-dojo-scope">{ui.roleLimits.senseiDojoScope}</p>
      )}
      <GsdReadableBlock>
        <p data-testid="sempai-role-desc">
          {locale === "en"
            ? "You assist classes and assigned groups. Full attendance tracking in Phase 2."
            : "Vous assistez les cours et groupes assignés. Suivi présences complet en Phase 2."}{" "}
          <DemoBadge />
        </p>
        <div className="home-grid">
          <ModuleCard title={locale === "en" ? "My dojo" : "Mon dojo"} testId="sempai-dojo-card">
            <p>{localizeDemo(dojo?.nom ?? "—")}</p>
          </ModuleCard>
          <ModuleCard title={locale === "en" ? "Assisted students" : "Étudiants assistés"} testId="sempai-students-card">
            <p>{students.length} {demoBadge}</p>
            <Link to="/sempai/etudiants">{locale === "en" ? "View" : "Voir"}</Link>
          </ModuleCard>
          <ModuleCard title={locale === "en" ? "Classes" : "Classes"} testId="sempai-classes-card">
            <Link to="/sempai/classes">{locale === "en" ? "Assisted classes" : "Classes assistées"}</Link>
          </ModuleCard>
          <ModuleCard title={locale === "en" ? "Attendance" : "Présences"} testId="sempai-presences-card">
            <Link to="/sempai/presences">{locale === "en" ? "Light attendance" : "Présences légères"}</Link>
          </ModuleCard>
          <ModuleCard title={locale === "en" ? "Progress" : "Progression"} testId="sempai-progress-card">
            <Link to="/sempai/progression">{locale === "en" ? "Consult progress" : "Consulter progression"}</Link>
          </ModuleCard>
          <ModuleCard title="Shotokan Black Dragon" testId="sempai-shotokan-card">
            <Link to="/sempai/shotokan-black-dragon">
              {locale === "en" ? "Titles and required proofs" : "Titres et preuves requises"}
            </Link>
          </ModuleCard>
        </div>
      </GsdReadableBlock>
    </PageSection>
  );
}

export function SempaiStudentsPage() {
  const { students } = useRoleContext();
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  return (
    <PageSection title={locale === "en" ? "Assisted students" : "Étudiants assistés"} testId="page-sempai-etudiants">
      <ul>
        {students.map((s) => (
          <li key={s.id}>
            {s.prenom} {s.nom} — {s.groupe} <DemoBadge />
          </li>
        ))}
      </ul>
    </PageSection>
  );
}

export function SempaiDocumentsPage() {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  return (
    <PageSection
      title={locale === "en" ? "Useful documents" : "Documents utiles"}
      subtitle={locale === "en" ? "Consultation only — demo Phase 1" : "Consultation seulement — démo Phase 1"}
      testId="page-sempai-documents"
    >
      <DojoOsModulePlaceholder moduleId="documents" locale={locale} />
    </PageSection>
  );
}

export function ParentDashboardPage() {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const { localizeDemo, ui } = useKarateUi();
  const { dojo, children, fromBackend, hasChildren } = useRoleContext();
  return (
    <PageSection
      title={locale === "en" ? "Parent space" : "Espace Parent"}
      subtitle={locale === "en" ? "Track your children — demo" : "Suivi de vos enfants — démo"}
      testId="page-parent"
    >
      <BackendZoneBadge variant={fromBackend ? "backendConnected" : "userPrepared"} testId="parent-dashboard-backend-badge" />
      <GsdReadableBlock>
        <p className="muted" data-testid="parent-linked-scope">{ui.roleLimits.parentLinkedChildren}</p>
        {!hasChildren ? (
          <p className="warning-box" data-testid="parent-no-children">{ui.roleLimits.parentNoChildren}</p>
        ) : null}
        <p className="muted" data-testid="parent-dashboard-no-payment">
          {locale === "en"
            ? "Summary only — payments not managed in Phase 1."
            : "Résumé uniquement — paiements non gérés en Phase 1."}
        </p>
        <div className="home-grid">
          <ModuleCard title={locale === "en" ? "My children" : "Mes enfants"} testId="parent-children-card">
            {children.length ? (
              <ul>
                {children.map((c) => (
                  <li key={c.id}>
                    {localizeDemo(c.prenom)} {localizeDemo(c.nom)} <DemoBadge />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted" data-testid="parent-children-empty">{ui.roleLimits.parentNoChildren}</p>
            )}
            <Link to="/parent/mes-enfants">{locale === "en" ? "Details" : "Détails"}</Link>
            {" · "}
            <Link to="/parent/adhesion">{locale === "en" ? "Membership" : "Adhésion"}</Link>
          </ModuleCard>
          <ModuleCard title={locale === "en" ? "Dojo" : "Dojo"} testId="parent-dojo-card">
            <p>{localizeDemo(dojo?.nom ?? "—")}</p>
          </ModuleCard>
          <ModuleCard title={locale === "en" ? "Schedule" : "Horaires"} testId="parent-schedule-card">
            <Link to="/parent/horaires">{locale === "en" ? "Children schedules" : "Horaires enfants"}</Link>
          </ModuleCard>
          <ModuleCard title={locale === "en" ? "Events" : "Événements"} testId="parent-events-card">
            <Link to="/parent/evenements">{locale === "en" ? "Upcoming events" : "Événements à venir"}</Link>
          </ModuleCard>
          <ModuleCard title={locale === "en" ? "Progress" : "Progression"} testId="parent-progress-card">
            <Link to="/parent/progression">{locale === "en" ? "Child progress" : "Progression enfant"}</Link>
          </ModuleCard>
        </div>
      </GsdReadableBlock>
    </PageSection>
  );
}

export function ParentChildrenPage() {
  const { children, dojo } = useRoleContext();
  const { lang } = useI18n();
  const { localizeDemo, ui } = useKarateUi();
  const locale = lang === "en" ? "en" : "fr";
  return (
    <PageSection
      title={locale === "en" ? "My children" : "Mes enfants"}
      subtitle={locale === "en" ? "Parent space — linked children only" : "Espace parent — enfants liés uniquement"}
      testId="page-parent-mes-enfants"
    >
      <GsdReadableBlock>
        <p className="muted" data-testid="parent-children-scope">{ui.roleLimits.parentLinkedChildren}</p>
        <p className="muted" data-testid="parent-children-no-payment">
          {locale === "en"
            ? "Summary only — payments not managed in Phase 1."
            : "Résumé uniquement — paiements non gérés en Phase 1."}
        </p>
        {children.length === 0 ? (
          <p className="warning-box" data-testid="parent-children-empty">{ui.roleLimits.parentNoChildren}</p>
        ) : (
          children.map((c) => (
            <GsdReadableBlock key={c.id} testId={`parent-child-${c.id}`}>
              <h3>
                {localizeDemo(c.prenom)} {localizeDemo(c.nom)} <DemoBadge />
              </h3>
              <p>
                {locale === "en" ? "Group" : "Groupe"} : {localizeDemo(c.groupe)} · {locale === "en" ? "Belt" : "Ceinture"} : {localizeDemo(c.ceinture)}
              </p>
              <p>
                {locale === "en" ? "Dojo" : "Dojo"} : {localizeDemo(dojo?.nom)}
              </p>
            </GsdReadableBlock>
          ))
        )}
      </GsdReadableBlock>
    </PageSection>
  );
}

export function ParentCommunicationsPage() {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  return (
    <PageSection
      title={locale === "en" ? "Communications" : "Communications"}
      subtitle={locale === "en" ? "Simple messages — demo Phase 1" : "Messages simples — démo Phase 1"}
      testId="page-parent-communications"
    >
      <DojoOsModulePlaceholder moduleId="communications" locale={locale} />
    </PageSection>
  );
}

export function ParentDocumentsPage() {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  return (
    <PageSection
      title={locale === "en" ? "Documents" : "Documents"}
      subtitle={locale === "en" ? "Authorized documents only — demo" : "Documents autorisés seulement — démo"}
      testId="page-parent-documents"
    >
      <DojoOsModulePlaceholder moduleId="documents" locale={locale} />
    </PageSection>
  );
}
